import React, { memo } from 'react';
import {
  ListItemButton, ListItemAvatar, ListItemText,
  Avatar, Checkbox, IconButton, Button, Box, Typography, Chip,
} from '@mui/material';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SellIcon from '@mui/icons-material/Sell';
import { ShoppingListGrocery } from '../types/ShoppingListGrocery';
import { Price } from '../types/Price';

interface ShoppingListItemProps {
  item: ShoppingListGrocery;
  price: Price | undefined;
  isCompleted: boolean;
  isMobile: boolean;
  onCheckboxClick: (item: ShoppingListGrocery) => void;
  onEnterPriceClick: (item: ShoppingListGrocery) => void;
  onRemoveClick: (item: ShoppingListGrocery) => void;
  onRemoveSaleClick?: (item: ShoppingListGrocery, price: Price) => void;
}

const ShoppingListItem: React.FC<ShoppingListItemProps> = ({
  item,
  price,
  isCompleted,
  isMobile,
  onCheckboxClick,
  onEnterPriceClick,
  onRemoveClick,
  onRemoveSaleClick,
}) => {
  const priceValue = price ? parseFloat(price.price) : null;
  const isPicked = item.picked === item.quantity;
  const isPartiallyPicked = item.picked > 0 && item.picked < item.quantity;

  return (
    <ListItemButton
      sx={{
        opacity: isPicked ? 0.6 : 1,
        bgcolor: isPicked ? 'action.selected' : 'transparent',
      }}
      disabled={isCompleted}
    >
      <Checkbox
        checked={isPicked}
        indeterminate={isPartiallyPicked}
        onChange={() => onCheckboxClick(item)}
        disabled={isCompleted}
      />
      <ListItemAvatar>
        <Avatar
          src={item.grocery_details.image_icon || `${process.env.REACT_APP_IMAGES_URL}/placeholder-grocery.svg`}
          variant="rounded"
          sx={{ width: 40, height: 40 }}
        />
      </ListItemAvatar>
      <ListItemText
        primary={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography sx={{ textDecoration: isPicked ? 'line-through' : 'none' }}>
              {item.grocery_details.name}
            </Typography>
            {isPartiallyPicked && (
              <Chip label={`${item.picked}/${item.quantity}`} size="small" color="default" />
            )}
            {!isPartiallyPicked && item.quantity > 1 && (
              <Chip label={`×${item.quantity}`} size="small" variant="outlined" />
            )}
          </Box>
        }
        secondary={
          <Box>
            <Typography variant="body2" color="text.secondary">
              {[item.grocery_details.brand, item.grocery_details.unit, item.grocery_details.packaging_size]
                .filter(Boolean)
                .join(' | ')}
            </Typography>
            {priceValue !== null && (
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                <Typography variant="body2" component="span">
                  ${priceValue.toFixed(2)} each
                </Typography>
                {price?.is_discounted && (
                  <>
                    <Chip
                      label={(() => {
                        if (!price.created_at) return 'On Sale';
                        const days = Math.floor((Date.now() - new Date(price.created_at).getTime()) / (1000 * 60 * 60 * 24));
                        if (days === 0) return 'On Sale (new today)';
                        if (days === 1) return 'On Sale (1 day)';
                        return `On Sale (${days} days)`;
                      })()}
                      size="small"
                      color="secondary"
                      sx={{ height: 18, fontSize: '0.7rem' }}
                    />
                    {price.price_before_discount && (
                      <Typography variant="body2" component="span" color="text.secondary">
                        was ${parseFloat(price.price_before_discount).toFixed(2)}
                      </Typography>
                    )}
                  </>
                )}
                <Typography variant="body2" component="span" color="text.secondary">
                  | ${(priceValue * item.quantity).toFixed(2)} total
                </Typography>
              </Box>
            )}
          </Box>
        }
      />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {!isCompleted && (
          <>
            {price?.is_discounted && onRemoveSaleClick && (
              isMobile ? (
                <IconButton
                  size="small"
                  color="warning"
                  onClick={() => onRemoveSaleClick(item, price)}
                  title="Remove sale"
                >
                  <SellIcon fontSize="small" sx={{ textDecoration: 'line-through' }} />
                </IconButton>
              ) : (
                <Button
                  variant="outlined"
                  size="small"
                  color="warning"
                  startIcon={<SellIcon sx={{ textDecoration: 'line-through' }} />}
                  onClick={() => onRemoveSaleClick(item, price)}
                  sx={{ minWidth: 'auto', whiteSpace: 'nowrap' }}
                >
                  End Sale
                </Button>
              )
            )}
            {isMobile ? (
              <IconButton
                size="small"
                onClick={() => onEnterPriceClick(item)}
                sx={!price ? { color: 'error.main' } : {}}
              >
                <AttachMoneyIcon />
              </IconButton>
            ) : (
              <Button
                variant="outlined"
                size="small"
                startIcon={<AttachMoneyIcon />}
                onClick={() => onEnterPriceClick(item)}
                sx={{
                  minWidth: 'auto',
                  whiteSpace: 'nowrap',
                  ...(!price && { color: 'error.main', borderColor: 'error.main' })
                }}
              >
                {price ? 'Update Price' : 'Add Price'}
              </Button>
            )}
            <IconButton
              size="small"
              onClick={() => onRemoveClick(item)}
              sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </>
        )}
      </Box>
    </ListItemButton>
  );
};

// Memoize to prevent re-renders when other items in the list change
export default memo(ShoppingListItem);
